<template>
  <label class="block my-3">
    {{ t(configs.label) }}
  </label>
  <div
    ref="parentElementRef"
    class="mb-3"
  >
    <VariablesButton
      :variables-list="enrichedVariables"
      @on-choose-variable="insertContent"
    />
  </div>
  <div
    v-if="configs.options.displayConditionalButton"
    class="mt-2 flex gap-2 flex-wrap"
  >
    <Button
      class="p-button-sm p-button-secondary conditional-button"
      :disabled="!isOnHtmlElement"
      @click="openConditionalModal"
    >
      <i class="fas fa-bolt conditional-icon fa-fw" />
      <span class="ml-2">{{ hasExistingSegments ? t('templateBuilder.conditionalDisplay.modifyCondition') : t('templateBuilder.conditionalDisplay.createCondition') }}</span>
    </Button>
    <span
      v-if="isProductsCollection"
      :title="t('templateBuilder.loop.productsCollectionTooltip')"
      class="loop-button-wrapper"
    >
      <Button
        class="p-button-sm p-button-secondary loop-button"
        :disabled="true"
        @click="openLoopModal"
      >
        <i class="fas fa-repeat loop-icon fa-fw" />
        <span class="ml-2">{{ hasExistingLoop ? t('templateBuilder.loop.modifyLoop') : t('templateBuilder.loop.createLoop') }}</span>
      </Button>
    </span>
    <Button
      v-else
      class="p-button-sm p-button-secondary loop-button"
      :disabled="!isOnHtmlElement"
      @click="openLoopModal"
    >
      <i class="fas fa-repeat loop-icon fa-fw" />
      <span class="ml-2">{{ hasExistingLoop ? t('templateBuilder.loop.modifyLoop') : t('templateBuilder.loop.createLoop') }}</span>
    </Button>
    <Button
      v-if="configs.options.mode !== 'javascript'"
      class="p-button-sm p-button-secondary"
      :disabled="!isCursorInHref"
      @click="openRedirectPicker"
    >
      <i class="fa-light fa-arrow-right-to-bracket fa-fw" />
      <span class="ml-2">{{ t('templateBuilder.wysiwygLinkDialog.insertRedirect') }}</span>
    </Button>
  </div>
  <div class="p-fluid">
    <div class="codemirror-wrapper">
      <div class="codemirror-header">
        <i
          class="fas fa-expand-alt expand-code-btn"
          :title="t('templateBuilder.codeEditor.expandToModal')"
          @click="openCodeModal"
        />
      </div>
      <VCodeMirror
        ref="codeMirrorEditorRef"
        v-model:value="inputValue"
        :mode="{ name: configs.options.mode || 'text/html' }"
        @blur="onBlur"
        @cursor-activity="onCursorActivity"
      />
    </div>
  </div>

  <ConditionalDisplayModal
    v-model:visible="isConditionalModalVisible"
    :model-value="currentElementSegments || [[{}]]"
    :context-element="selectedHtmlElement"
    @validate="handleConditionalSave"
    @delete="handleConditionalDelete"
  />

  <LoopConfigurationModal
    v-model:visible="isLoopModalVisible"
    :model-value="currentLoopConfig"
    :title="hasExistingLoop ? t('templateBuilder.loop.modifyLoop') : t('templateBuilder.loop.createLoop')"
    :show-delete-button="hasExistingLoop"
    @save="handleLoopSave"
    @delete="handleLoopDelete"
  />

  <!-- Redirect picker — funnel-only, no URL mode -->
  <WysiwygLinkDialog
    :visible="isRedirectPickerVisible"
    :initial-url="currentHrefValue"
    :show-funnel-option="true"
    :funnel-only="true"
    @update:visible="isRedirectPickerVisible = $event"
    @confirm="handleRedirectConfirm"
    @remove="handleRedirectRemove"
  />

  <!-- Modal CodeMirror -->
  <CodeMirrorModal
    v-model:visible="showCodeModal"
    v-model:code="inputValue"
    :language="configs.options.mode === 'javascript' ? 'javascript' : 'html'"
    :title="configs.options.mode === 'javascript' ? t('templateBuilder.fields.javascript.modalTitle') : t('templateBuilder.fields.code.modalTitle')"
    :show-variables="configs.options.displayVariables"
    :variables-list="configs.options.variablesList || []"
    @apply="handleModalApply"
  />
</template>

<script lang="ts">
import {
  defineComponent, nextTick, onMounted, PropType, Ref, ref, watch, computed,
} from 'vue';
import { VCodeMirror } from 'vue3-code-mirror';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import { FieldConfig, ParserFieldObject, Property } from '@/types';
import type { LoopConfiguration } from '@/types/loop-configuration';

import 'codemirror/mode/htmlmixed/htmlmixed';
import 'codemirror/mode/javascript/javascript';
import beautifier from 'js-beautify';
import VariablesButton from '@/components/fields/partials/VariablesButton.vue';
import WysiwygLinkDialog from '@/components/template-builder/fields/WysiwygLinkDialog.vue';
import ConditionalDisplayModal from '@/components/conditional-display/ConditionalDisplayModal.vue';
import LoopConfigurationModal from '@/components/template-builder/modals/LoopConfigurationModal.vue';
import { getVariablesWithCollectionContext, getVariablesForCollection, getMetadataVariables } from '@/composables/template-builder/collectionContext';
import { refreshNextSteps, updateTemplateConfigurationNextIdTemplate } from '@/composables/template-editor/TemplateEditor';
import { getCustomContactProperties } from '@/configs/custom-contact-properties';
import CodeMirrorModal from '@/components/common/CodeMirrorModal.vue';

export default defineComponent({
  name: 'Code',
  components: {
    VCodeMirror,
    Button,
    VariablesButton,
    WysiwygLinkDialog,
    ConditionalDisplayModal,
    LoopConfigurationModal,
    CodeMirrorModal,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const properties: Ref<Array<Property>> = ref(props.parserValues.properties ?? []);
    const inputValue: Ref<string> = ref(properties.value[0].value);
    const cursorPosition = ref({ line: 0, ch: 0 });
    const codeMirrorEditorRef = ref();
    const enrichedVariables = ref(props.configs.options.variablesList || []);
    const baseVariablesWithCustomAndMetadata = ref<any[]>([]);
    const showCodeModal = ref(false);
    let lastScrollPosition = { top: 0, left: 0 };

    // Conditional display modal
    const isConditionalModalVisible = ref(false);
    const selectedHtmlElement = ref<HTMLElement | null>(null);
    const currentElementSegments = ref<any>(null);
    const selectedElementInfo = ref<{ tagName: string; startLine: number; endLine: number } | null>(null);

    // Loop configuration modal
    const isLoopModalVisible = ref(false);
    const currentLoopConfig = ref<LoopConfiguration | null>(null);

    // Store conditions with their placeholders
    const conditionsMap = ref<Map<string, any>>(new Map());
    let conditionCounter = 0;

    // Store collections with their placeholders
    const collectionsMap = ref<Map<string, any>>(new Map());

    // Replace s-segments with placeholders
    const replaceSegmentsWithPlaceholders = (html: string): string => {
      conditionsMap.value.clear();
      conditionCounter = 0;
      collectionsMap.value.clear();

      let result = html;

      // Replace collections (except products) - handle any order and separation
      // Find all opening tags with both s-collection and s-item attributes
      const tagRegex = /<([a-zA-Z][a-zA-Z0-9]*)[^>]*>/gs;
      result = result.replace(tagRegex, (match) => {
        // Check if tag has both s-collection and s-item
        const collectionMatch = match.match(/s-collection="([^"]*)"/);
        const itemMatch = match.match(/s-item="([^"]*)"/);

        if (collectionMatch && itemMatch) {
          try {
            const collection = collectionMatch[1];
            const item = itemMatch[1];

            // Parse collection (can be a string or an object)
            let collectionData;
            try {
              collectionData = JSON.parse(collection.replace(/&quot;/g, '"'));
            } catch {
              // If not JSON, it's a plain string
              collectionData = collection;
            }

            // Skip products collection
            const collectionName = typeof collectionData === 'object' && collectionData.name
              ? collectionData.name
              : collectionData;

            if (collectionName === 'products') {
              return match;
            }

            const placeholder = `ITERATION ${item}`;

            // Extract s-order and s-nb if they exist
            const orderMatch = match.match(/s-order="([^"]*)"/);
            const nbMatch = match.match(/s-nb="([^"]*)"/);

            const collectionMapData: any = { collection: collectionData, item };
            if (orderMatch) {
              try {
                collectionMapData.order = JSON.parse(orderMatch[1].replace(/&quot;/g, '"'));
              } catch (e) {
                // Ignore invalid JSON
              }
            }
            if (nbMatch) {
              collectionMapData.limit = parseInt(nbMatch[1], 10);
            }

            collectionsMap.value.set(placeholder, collectionMapData);

            // Remove s-collection, s-item, s-order, and s-nb attributes, keep all others
            let cleanedTag = match.replace(/\s*s-collection="[^"]*"/, '');
            cleanedTag = cleanedTag.replace(/\s*s-item="[^"]*"/, '');
            cleanedTag = cleanedTag.replace(/\s*s-order="[^"]*"/, '');
            cleanedTag = cleanedTag.replace(/\s*s-nb="[^"]*"/, '');

            // Insert placeholder before the closing > (keeping all other attributes intact)
            cleanedTag = cleanedTag.replace(/>$/, ` ${placeholder}>`);

            return cleanedTag;
          } catch (e) {
            return match;
          }
        }

        return match;
      });

      // Replace conditions
      result = result.replace(/s-segments="([^"]*)"/g, (match, segmentsJson) => {
        try {
          const decoded = segmentsJson.replace(/&quot;/g, '"');
          const segments = JSON.parse(decoded);
          conditionCounter += 1;
          const placeholder = `CONDITION ${conditionCounter}`;
          conditionsMap.value.set(placeholder, segments);
          return placeholder;
        } catch (e) {
          return match;
        }
      });

      return result;
    };

    // Restore placeholders with actual segments and collections
    const restorePlaceholdersWithSegmentsAndCollections = (html: string): string => {
      // Restore collections
      let result = html.replace(/ITERATION ([a-zA-Z_][a-zA-Z0-9_]*)/g, (match, iteratorName) => {
        const placeholder = `ITERATION ${iteratorName}`;
        const data = collectionsMap.value.get(placeholder);
        if (data) {
          const collectionJson = JSON.stringify(data.collection).replace(/"/g, '&quot;');
          let attributes = `s-collection="${collectionJson}" s-item="${data.item}"`;

          // Add s-order if present
          if (data.order) {
            const orderJson = JSON.stringify(data.order).replace(/"/g, '&quot;');
            attributes += ` s-order="${orderJson}"`;
          }

          // Add s-nb if present
          if (data.limit) {
            attributes += ` s-nb="${data.limit}"`;
          }

          return attributes;
        }
        return match;
      });

      // Restore conditions
      result = result.replace(/CONDITION (\d+)/g, (match, num) => {
        const placeholder = `CONDITION ${num}`;
        const segments = conditionsMap.value.get(placeholder);
        if (segments) {
          const json = JSON.stringify(segments).replace(/"/g, '&quot;');
          return `s-segments="${json}"`;
        }
        return match;
      });

      return result;
    };

    const restoreScroll = () => {
      if (codeMirrorEditorRef.value && codeMirrorEditorRef.value?.editor) {
        setTimeout(() => {
          codeMirrorEditorRef.value.editor.scrollTo(lastScrollPosition.left, lastScrollPosition.top);
        }, 200);
      }
    };

    const handleValueChange = () => {
      // Restore placeholders to actual segments before saving
      const restoredHtml = restorePlaceholdersWithSegmentsAndCollections(inputValue.value);

      const updatedProperties: Array<Property> = properties.value.map((prop: Property) => {
        const updatedProperty = prop;
        updatedProperty.value = restoredHtml;
        return updatedProperty;
      });

      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: updatedProperties,
      });
    };

    const insertContent = (value: string|number|undefined) => {
      if (codeMirrorEditorRef.value) {
        codeMirrorEditorRef.value.editor.getDoc().replaceRange(value, cursorPosition.value);
        handleValueChange();
      }
    };

    // ---- Redirect picker for href attributes ----

    const isRedirectPickerVisible = ref(false);
    const currentHrefValue = ref('');
    // Stores the range of the href value (between quotes) for replacement
    const hrefValueRange = ref<{ from: { line: number; ch: number }; to: { line: number; ch: number } } | null>(null);

    // Detect if cursor is inside an href="..." attribute value
    const isCursorInHref = computed(() => {
      if (!codeMirrorEditorRef.value?.editor) return false;
      const editor = codeMirrorEditorRef.value.editor;
      const cursor = cursorPosition.value;
      const lineContent = editor.getLine(cursor.line);
      if (!lineContent) return false;

      // Find all href="..." in the line
      const hrefRegex = /href\s*=\s*"([^"]*)"/gi;
      let match;
      // eslint-disable-next-line no-cond-assign
      while ((match = hrefRegex.exec(lineContent)) !== null) {
        const valueStart = match.index + match[0].indexOf('"') + 1;
        const valueEnd = valueStart + match[1].length;
        if (cursor.ch >= valueStart && cursor.ch <= valueEnd) {
          return true;
        }
      }
      return false;
    });

    const openRedirectPicker = () => {
      if (!codeMirrorEditorRef.value?.editor) return;
      const editor = codeMirrorEditorRef.value.editor;
      const cursor = cursorPosition.value;
      const lineContent = editor.getLine(cursor.line);

      // Find the href="..." the cursor is in and extract its range
      const hrefRegex = /href\s*=\s*"([^"]*)"/gi;
      let match;
      // eslint-disable-next-line no-cond-assign
      while ((match = hrefRegex.exec(lineContent)) !== null) {
        const valueStart = match.index + match[0].indexOf('"') + 1;
        const valueEnd = valueStart + match[1].length;
        if (cursor.ch >= valueStart && cursor.ch <= valueEnd) {
          currentHrefValue.value = match[1];
          hrefValueRange.value = {
            from: { line: cursor.line, ch: valueStart },
            to: { line: cursor.line, ch: valueEnd },
          };
          isRedirectPickerVisible.value = true;
          return;
        }
      }
    };

    /**
     * Extract the base64 redirect ID from a href value.
     * e.g. "#redirect_MTU3" → "MTU3", "#spin_redirect_MTU3" → "MTU3"
     */
    const extractRedirectId = (href: string): string => {
      if (href.includes('#spin_redirect_')) return href.split('#spin_redirect_')[1] || '';
      if (href.includes('#redirect_')) return href.split('#redirect_')[1] || '';
      return '';
    };

    /**
     * Add or update `data-redirect="XXX"` on the tag that contains the href.
     * The backend and refreshNextSteps() scan for data-redirect, not href="#redirect_".
     */
    const upsertDataRedirectOnTag = (editor: any, line: number, redirectId: string) => {
      const lineContent = editor.getLine(line);

      // Check if data-redirect already exists on this line
      const existingMatch = lineContent.match(/data-redirect="([^"]*)"/);
      if (existingMatch) {
        // Update existing data-redirect value
        const attrStart = lineContent.indexOf('data-redirect="') + 'data-redirect="'.length;
        const attrEnd = attrStart + existingMatch[1].length;
        editor.getDoc().replaceRange(redirectId, { line, ch: attrStart }, { line, ch: attrEnd });
      } else {
        // Insert data-redirect after href="..."
        // Find the closing quote of href value, then insert after it
        const hrefEndMatch = lineContent.match(/href="[^"]*"/);
        if (hrefEndMatch && hrefEndMatch.index !== undefined) {
          const insertPos = hrefEndMatch.index + hrefEndMatch[0].length;
          editor.getDoc().replaceRange(` data-redirect="${redirectId}"`, { line, ch: insertPos }, { line, ch: insertPos });
        }
      }
    };

    /**
     * Remove `data-redirect="..."` from the tag on the given line.
     */
    const removeDataRedirectFromTag = (editor: any, line: number) => {
      const lineContent = editor.getLine(line);
      const match = lineContent.match(/\s*data-redirect="[^"]*"/);
      if (match && match.index !== undefined) {
        const start = match.index;
        const end = start + match[0].length;
        editor.getDoc().replaceRange('', { line, ch: start }, { line, ch: end });
      }
    };

    const handleRedirectConfirm = (result: { type: string; url: string }) => {
      if (!codeMirrorEditorRef.value?.editor || !hrefValueRange.value) return;
      const editor = codeMirrorEditorRef.value.editor;
      const line = hrefValueRange.value.from.line;

      // Replace the href value content with the redirect URL
      editor.getDoc().replaceRange(result.url, hrefValueRange.value.from, hrefValueRange.value.to);

      // Add/update data-redirect attribute (needed by backend and refreshNextSteps)
      const redirectId = extractRedirectId(result.url);
      if (redirectId) {
        upsertDataRedirectOnTag(editor, line, redirectId);
      }

      handleValueChange();

      // Wait for the iframe DOM to be updated (setContent is called via handleValueChange),
      // then refresh funnel state and update nextIdTemplate in config
      nextTick(() => {
        setTimeout(() => {
          refreshNextSteps();
          updateTemplateConfigurationNextIdTemplate();
        }, 500);
      });
    };

    const handleRedirectRemove = () => {
      if (!codeMirrorEditorRef.value?.editor || !hrefValueRange.value) return;
      const editor = codeMirrorEditorRef.value.editor;
      const line = hrefValueRange.value.from.line;

      // Clear the href value
      editor.getDoc().replaceRange('', hrefValueRange.value.from, hrefValueRange.value.to);

      // Remove data-redirect attribute
      removeDataRedirectFromTag(editor, line);

      handleValueChange();

      nextTick(() => {
        setTimeout(() => {
          refreshNextSteps();
          updateTemplateConfigurationNextIdTemplate();
        }, 500);
      });
    };

    // Detect if element is inside a collection
    const detectCollectionContext = (startLine: number): { name: string; iterator: string } | null => {
      // Helper to parse collection from s-collection attribute
      const parseCollectionAttribute = (collectionValue: string, explicitIterator: string | null) => {
        try {
          const decoded = collectionValue
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/'/g, '"');
          const config = JSON.parse(decoded);
          const collectionName = config.name || config.collection || '';
          const iterator = explicitIterator || config.iterator || config.as || collectionName.slice(0, -1);
          return { name: collectionName, iterator };
        } catch (e) {
          const collectionName = collectionValue;
          const iterator = explicitIterator || (collectionName.endsWith('s') ? collectionName.slice(0, -1) : collectionName);
          return { name: collectionName, iterator };
        }
      };

      // First, check the current line itself
      const currentLine = codeMirrorEditorRef.value?.editor.getLine(startLine) || '';

      // Check for ITERATION placeholder (transformed s-collection)
      const currentIterationMatch = currentLine.match(/ITERATION ([a-zA-Z_][a-zA-Z0-9_]*)/);
      if (currentIterationMatch) {
        const placeholder = `ITERATION ${currentIterationMatch[1]}`;
        const data = collectionsMap.value.get(placeholder);
        if (data) {
          const collectionName = typeof data.collection === 'string' ? data.collection : data.collection.name;
          return { name: collectionName, iterator: data.item };
        }
      }

      // Check for s-collection attribute
      const currentCollectionMatch = currentLine.match(/s-collection=["']([^"']*)["']/);
      const currentItemMatch = currentLine.match(/s-item=["']([^"']*)["']/);
      if (currentCollectionMatch && currentItemMatch) {
        return parseCollectionAttribute(currentCollectionMatch[1], currentItemMatch[1]);
      }

      // If not found on current line, search backwards through all lines
      for (let i = startLine - 1; i >= 0; i -= 1) {
        const line = codeMirrorEditorRef.value?.editor.getLine(i) || '';

        // Check for ITERATION placeholder first
        const iterationMatch = line.match(/ITERATION ([a-zA-Z_][a-zA-Z0-9_]*)/);
        if (iterationMatch) {
          const placeholder = `ITERATION ${iterationMatch[1]}`;
          const data = collectionsMap.value.get(placeholder);
          if (data) {
            const collectionName = typeof data.collection === 'string' ? data.collection : data.collection.name;
            return { name: collectionName, iterator: data.item };
          }
        }

        // Check for s-collection attribute
        const collectionMatch = line.match(/s-collection=["']([^"']*)["']/);
        if (collectionMatch) {
          const itemMatch = line.match(/s-item=["']([^"']*)["']/);
          return parseCollectionAttribute(collectionMatch[1], itemMatch ? itemMatch[1] : null);
        }
      }
      return null;
    };

    // Update variables based on cursor context (detect collections)
    const updateVariablesForContext = async (cursorLine: number, cursorCh?: number) => {
      if (baseVariablesWithCustomAndMetadata.value.length === 0) return;

      try {
        // Start with base variables enriched with custom and metadata
        let currentVariables = baseVariablesWithCustomAndMetadata.value;

        // First, enrich with the widget's parent collection context (if exists)
        if (props.parserValues.selector) {
          currentVariables = await getVariablesWithCollectionContext(
            props.parserValues.selector,
            baseVariablesWithCustomAndMetadata.value,
          ) as never[];
        }

        // Then, detect collections in the HTML code at cursor position
        const allLines = inputValue.value.split('\n');
        const cursorCharPosition = cursorCh ?? 0;

        // Find all s-collection attributes OR ITERATION placeholders in the content
        const collectionRegex = /s-collection="([^"]+)"/g;
        const itemRegex = /s-item="([^"]+)"/g;
        const iterationPlaceholderRegex = /ITERATION ([a-zA-Z_][a-zA-Z0-9_]*)/g;

        const collections: Array<{ collection: string; iterator: string }> = [];
        const collectionSet = new Set<string>();

        // Track open collections with their tag names
        const openCollections: Array<{
          collection: string;
          iterator: string;
          startLine: number;
          tagName: string;
        }> = [];

        // Parse all lines to track collection scopes
        for (let i = 0; i <= cursorLine; i += 1) {
          const line = allLines[i];

          // Check for ITERATION placeholder first (used in code editor view)
          const iterationMatch = line.match(iterationPlaceholderRegex);
          if (iterationMatch) {
            const iteratorName = iterationMatch[0].replace('ITERATION ', '');
            const placeholder = `ITERATION ${iteratorName}`;
            const data = collectionsMap.value.get(placeholder);

            if (data) {
              const collectionName = typeof data.collection === 'string'
                ? data.collection
                : (data.collection.name || data.collection);

              // Extract tag name
              const tagNameMatch = line.match(/<([a-zA-Z][a-zA-Z0-9]*)[^>]*ITERATION/);
              const tagName = tagNameMatch ? tagNameMatch[1] : null;

              // Find the position of the closing > of the opening tag
              const openingTagMatch = line.match(/<[a-zA-Z][a-zA-Z0-9]*[^>]*ITERATION[^>]*>/);
              const openingTagEndPosition = openingTagMatch
                ? (line.indexOf(openingTagMatch[0]) + openingTagMatch[0].length)
                : 0;

              if (tagName) {
                const closingTagRegex = new RegExp(`</${tagName}>`);
                const closesOnSameLine = closingTagRegex.test(line);
                const cursorAfterOpeningTag = i === cursorLine && cursorCharPosition >= openingTagEndPosition;

                if (!closesOnSameLine || cursorAfterOpeningTag) {
                  openCollections.push({
                    collection: collectionName,
                    iterator: data.item,
                    startLine: i,
                    tagName,
                  });
                }
              }
            }
          }

          // Also check for s-collection attributes (original format)
          const collectionMatch = line.match(collectionRegex);
          const itemMatch = line.match(itemRegex);

          if (collectionMatch && itemMatch) {
            const collection = collectionMatch[0].match(/"([^"]+)"/)?.[1];
            const iterator = itemMatch[0].match(/"([^"]+)"/)?.[1];

            // Extract tag name and find the opening tag end position
            const tagNameMatch = line.match(/<([a-zA-Z][a-zA-Z0-9]*)[^>]*s-collection=/);
            const tagName = tagNameMatch ? tagNameMatch[1] : null;

            // Find the position of the closing > of the opening tag
            const openingTagMatch = line.match(/<[a-zA-Z][a-zA-Z0-9]*[^>]*s-collection=[^>]*>/);
            const openingTagEndPosition = openingTagMatch
              ? (line.indexOf(openingTagMatch[0]) + openingTagMatch[0].length)
              : 0;

            if (collection && iterator && tagName) {
              // Check if the tag closes on the same line
              const closingTagRegex = new RegExp(`</${tagName}>`);
              const closesOnSameLine = closingTagRegex.test(line);

              // Only add to open collections if:
              // 1. It doesn't close on the same line, OR
              // 2. Cursor is on this line AND after the opening tag (>= to include position right after >)
              const cursorAfterOpeningTag = i === cursorLine && cursorCharPosition >= openingTagEndPosition;

              if (!closesOnSameLine || cursorAfterOpeningTag) {
                openCollections.push({
                  collection,
                  iterator,
                  startLine: i,
                  tagName,
                });
              }
            }
          }

          // Check for closing tags and remove corresponding collections
          const closingTagRegex = /<\/([a-zA-Z][a-zA-Z0-9]*)>/g;
          let closingMatch;
          // eslint-disable-next-line no-cond-assign
          while ((closingMatch = closingTagRegex.exec(line)) !== null) {
            const closingTagName = closingMatch[1];
            const closingTagEndPosition = (closingMatch.index || 0) + closingMatch[0].length;

            // Remove the last collection with this tag name
            for (let j = openCollections.length - 1; j >= 0; j -= 1) {
              if (openCollections[j].tagName === closingTagName) {
                // Remove if:
                // 1. We're past the opening line, OR
                // 2. We're on the same line as opening but not on cursor line, OR
                // 3. We're on cursor line but cursor is after the closing tag
                const shouldRemove = i > openCollections[j].startLine
                  || (i === openCollections[j].startLine && i !== cursorLine)
                  || (i === cursorLine && cursorCharPosition >= closingTagEndPosition);

                if (shouldRemove) {
                  openCollections.splice(j, 1);
                  break;
                }
              }
            }
          }
        }

        // Add only the collections that are still open at cursor position
        openCollections.forEach(({ collection, iterator }) => {
          const key = `${collection}::${iterator}`;
          if (!collectionSet.has(key)) {
            collectionSet.add(key);
            collections.push({ collection, iterator });
          }
        });

        // Enrich with all collections found in the code (from outermost to innermost)
        if (collections.length > 0) {
          const enrichedWithCollections = await collections.reduce(
            async (prevPromise, { collection, iterator }) => {
              const vars = await prevPromise;
              return getVariablesForCollection(collection, iterator, vars) as Promise<never[]>;
            },
            Promise.resolve(currentVariables as never[]),
          );
          enrichedVariables.value = enrichedWithCollections;
        } else {
          enrichedVariables.value = currentVariables;
        }
      } catch (error) {
        console.error('Error enriching variables:', error);
        enrichedVariables.value = baseVariablesWithCustomAndMetadata.value as never[];
      }
    };

    // Detect HTML element at cursor position
    const onCursorActivity = (editor: any) => {
      const cursor = editor.getCursor();
      cursorPosition.value = { line: cursor.line, ch: cursor.ch };

      // Update variables based on cursor context
      updateVariablesForContext(cursor.line, cursor.ch);

      // Get the current line content
      const lineContent = editor.getLine(cursor.line);
      const cursorChar = cursor.ch;

      // Find all tags in the line
      const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9]*)[^>]*>/g;
      const matches = Array.from(lineContent.matchAll(tagRegex)) as RegExpMatchArray[];

      // Find if cursor is on any tag (between < and > exclusively)
      const matchedTag = matches.find((match) => {
        const tagStart = match.index || 0;
        const tagEnd = tagStart + match[0].length;
        // Cursor must be strictly inside the tag: after < and before >
        return cursorChar > tagStart && cursorChar < tagEnd;
      });

      if (matchedTag) {
        selectedElementInfo.value = {
          tagName: matchedTag[1],
          startLine: cursor.line,
          endLine: cursor.line,
        };

        // Check if tag has CONDITION placeholder or s-segments attribute
        const tagContent = matchedTag[0];
        const conditionMatch = tagContent.match(/CONDITION (\d+)/);
        const segmentsMatch = tagContent.match(/s-segments="([^"]*)"/);
        if (conditionMatch) {
          // It's a CONDITION placeholder
          const placeholder = `CONDITION ${conditionMatch[1]}`;
          currentElementSegments.value = conditionsMap.value.get(placeholder) || null;
        } else if (segmentsMatch) {
          const segmentsValue = segmentsMatch[1];
          // It's a JSON string
          try {
            const decodedSegments = segmentsValue.replace(/&quot;/g, '"');
            currentElementSegments.value = JSON.parse(decodedSegments);
          } catch (e) {
            currentElementSegments.value = null;
          }
        } else {
          currentElementSegments.value = null;
        }

        // Check if tag has ITERATION placeholder or s-collection and s-item attributes (loop configuration)
        const iterationMatch = tagContent.match(/ITERATION ([a-zA-Z_][a-zA-Z0-9_]*)/);
        if (iterationMatch) {
          // It's an ITERATION placeholder
          const placeholder = `ITERATION ${iterationMatch[1]}`;
          const data = collectionsMap.value.get(placeholder);
          if (data) {
            const collectionValue = typeof data.collection === 'string'
              ? data.collection
              : JSON.stringify(data.collection);
            currentLoopConfig.value = {
              enabled: true,
              collection: collectionValue,
              iterator: data.item,
              order: data.order,
              limit: data.limit,
            };
          } else {
            currentLoopConfig.value = null;
          }
        } else {
          const collectionMatch = tagContent.match(/s-collection="([^"]+)"/);
          const itemMatch = tagContent.match(/s-item="([^"]+)"/);
          const orderMatch = tagContent.match(/s-order="([^"]+)"/);
          const nbMatch = tagContent.match(/s-nb="([^"]+)"/);
          if (collectionMatch && itemMatch) {
            const config: LoopConfiguration = {
              enabled: true,
              collection: collectionMatch[1],
              iterator: itemMatch[1],
            };
            if (orderMatch) {
              try {
                config.order = JSON.parse(orderMatch[1].replace(/&quot;/g, '"'));
              } catch (e) {
                // Ignore invalid JSON
              }
            }
            if (nbMatch) {
              config.limit = parseInt(nbMatch[1], 10);
            }
            currentLoopConfig.value = config;
          } else {
            currentLoopConfig.value = null;
          }
        }
      } else {
        selectedElementInfo.value = null;
        currentElementSegments.value = null;
        currentLoopConfig.value = null;
      }
    };

    const onBlur = (editor: Record<string, Function>) => {
      lastScrollPosition = { top: editor.getScrollInfo().top, left: editor.getScrollInfo().left };
      cursorPosition.value = {
        line: editor.getCursor().line,
        ch: editor.getCursor().ch,
      };

      const currentValue = inputValue.value;
      let newValue;
      properties.value.map((prop: Property) => {
        newValue = prop.value;
        return prop;
      });

      if (currentValue !== newValue) {
        handleValueChange();
      }
    };

    // Create clickable iteration widget (cyan)
    const createIterationWidget = (placeholder: string) => {
      const widget = document.createElement('span');
      widget.className = 'iteration-placeholder';
      widget.innerHTML = `<i class="fas fa-repeat" style="margin-right: 4px;"></i>${placeholder}`;
      widget.title = t('templateBuilder.loop.modifyLoop');
      widget.style.cssText = `
        background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
        color: white;
        padding: 3px 10px;
        border-radius: 4px;
        cursor: pointer;
        font-weight: 600;
        font-size: 11px;
        display: inline-flex;
        align-items: center;
        margin: 0 2px;
        transition: all 0.2s;
        box-shadow: 0 2px 4px rgba(139, 92, 246, 0.3);
      `;

      widget.addEventListener('mouseenter', () => {
        widget.style.background = 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)';
        widget.style.transform = 'scale(1.05)';
        widget.style.boxShadow = '0 4px 8px rgba(6, 182, 212, 0.4)';
      });

      widget.addEventListener('mouseleave', () => {
        widget.style.background = 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)';
        widget.style.transform = 'scale(1)';
        widget.style.boxShadow = '0 2px 4px rgba(6, 182, 212, 0.3)';
      });

      widget.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Load collection from map
        const data = collectionsMap.value.get(placeholder);
        if (data) {
          // If collection is already a string, use it directly; otherwise stringify it
          const collectionValue = typeof data.collection === 'string'
            ? data.collection
            : JSON.stringify(data.collection);

          currentLoopConfig.value = {
            enabled: true,
            collection: collectionValue,
            iterator: data.item,
            order: data.order,
            limit: data.limit,
          };
          isLoopModalVisible.value = true;
        }
      });

      return widget;
    };

    // Create clickable placeholder widget (violet)
    const createPlaceholderWidget = (placeholder: string) => {
      const widget = document.createElement('span');
      widget.className = 'condition-placeholder';
      widget.innerHTML = `<i class="fas fa-bolt" style="margin-right: 4px;"></i>${placeholder.replace(/_/g, ' ')}`;
      widget.title = t('templateBuilder.conditionalDisplay.modifyCondition');
      widget.style.cssText = `
        background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
        color: white;
        padding: 3px 10px;
        border-radius: 4px;
        cursor: pointer;
        font-weight: 600;
        font-size: 11px;
        display: inline-flex;
        align-items: center;
        margin: 0 2px;
        transition: all 0.2s;
        box-shadow: 0 2px 4px rgba(139, 92, 246, 0.3);
      `;

      widget.addEventListener('mouseenter', () => {
        widget.style.background = 'linear-gradient(135deg, #7c3aed 0%, #9333ea 100%)';
        widget.style.transform = 'scale(1.05)';
        widget.style.boxShadow = '0 4px 8px rgba(139, 92, 246, 0.4)';
      });

      widget.addEventListener('mouseleave', () => {
        widget.style.background = 'linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)';
        widget.style.transform = 'scale(1)';
        widget.style.boxShadow = '0 2px 4px rgba(139, 92, 246, 0.3)';
      });

      widget.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Load condition from map
        const segments = conditionsMap.value.get(placeholder);
        if (segments) {
          currentElementSegments.value = segments;
          isConditionalModalVisible.value = true;
        }
      });

      return widget;
    };

    // Add placeholder widgets to CodeMirror
    const addPlaceholderWidgets = (editor: any) => {
      // Clear existing widgets
      editor.getAllMarks().forEach((mark: any) => {
        if (mark.className === 'condition-placeholder-mark' || mark.className === 'iteration-placeholder-mark') {
          mark.clear();
        }
      });

      // Find all placeholders in the document
      const doc = editor.getDoc();
      const lineCount = doc.lineCount();

      for (let i = 0; i < lineCount; i += 1) {
        const line = doc.getLine(i);

        // Find ITERATION placeholders
        const iterationRegex = /ITERATION ([a-zA-Z_][a-zA-Z0-9_]*)/g;
        let iterationMatch = iterationRegex.exec(line);

        while (iterationMatch !== null) {
          const placeholder = iterationMatch[0]; // "ITERATION 1"
          const start = iterationMatch.index;
          const end = start + placeholder.length;

          // Replace text with widget
          const widget = createIterationWidget(placeholder);
          doc.markText(
            { line: i, ch: start },
            { line: i, ch: end },
            {
              replacedWith: widget,
              handleMouseEvents: true,
              className: 'iteration-placeholder-mark',
            },
          );

          iterationMatch = iterationRegex.exec(line);
        }

        // Find CONDITION placeholders
        const conditionRegex = /CONDITION (\d+)/g;
        let conditionMatch = conditionRegex.exec(line);

        while (conditionMatch !== null) {
          const placeholder = conditionMatch[0]; // "CONDITION 1"
          const start = conditionMatch.index;
          const end = start + placeholder.length;

          // Replace text with widget
          const widget = createPlaceholderWidget(placeholder);
          doc.markText(
            { line: i, ch: start },
            { line: i, ch: end },
            {
              replacedWith: widget,
              handleMouseEvents: true,
              className: 'condition-placeholder-mark',
            },
          );

          conditionMatch = conditionRegex.exec(line);
        }
      }
    };

    onMounted(async () => {
      if (codeMirrorEditorRef.value) {
        lastScrollPosition = { top: 0, left: 0 };
        codeMirrorEditorRef.value.editor.setOption('theme', 'default');

        // Add placeholder widgets
        addPlaceholderWidgets(codeMirrorEditorRef.value.editor);

        // Listen to cursor activity
        codeMirrorEditorRef.value.editor.on('cursorActivity', (editor: any) => {
          onCursorActivity(editor);
        });

        // Re-add widgets on content change
        codeMirrorEditorRef.value.editor.on('change', (editor: any) => {
          setTimeout(() => {
            addPlaceholderWidgets(editor);
          }, 100);
        });
      }

      // Enrich variables with collection context
      let baseVariables = props.configs.options.variablesList || [];

      // Add custom contact properties
      try {
        const customSchemas = await getCustomContactProperties();
        const customVariables = customSchemas.flatMap((schema) => schema.properties.map((prop) => ({
          value: `{var=${prop.variableName}}`,
          label: `${schema.schemaName} - ${prop.name}`,
          labelWithBadge: `${schema.schemaName} - ${prop.name} <span class="custom-badge-inline">CUSTOM</span>`,
          type: prop.type,
          isCustom: true,
        })));
        baseVariables = [...baseVariables, ...customVariables];
      } catch (error) {
        console.error('Error loading custom contact properties:', error);
      }

      // Add metadata variables from custom events
      try {
        const metadataVars = await getMetadataVariables();
        baseVariables = [...baseVariables, ...metadataVars];
      } catch (error) {
        console.error('Error loading metadata variables:', error);
      }

      // Store base variables with custom and metadata for use in updateVariablesForContext
      baseVariablesWithCustomAndMetadata.value = baseVariables;

      if (props.parserValues.selector) {
        enrichedVariables.value = await getVariablesWithCollectionContext(
          props.parserValues.selector,
          baseVariables,
        ) as never[];
      } else {
        enrichedVariables.value = baseVariables as never[];
      }
    });

    watch(() => props.parserValues.properties, (newProperties, oldProperties) => {
      if (newProperties && newProperties[0] && newProperties[0].value !== inputValue.value) {
        const beautifiedHtml = beautifier.html(newProperties[0].value);
        inputValue.value = replaceSegmentsWithPlaceholders(beautifiedHtml);
        restoreScroll();
      }
    }, { deep: true, immediate: true });

    // Also watch the specific value for additional safety
    watch(() => properties.value[0].value, (values, oldValues) => {
      if (values !== oldValues && values !== inputValue.value) {
        const beautifiedHtml = beautifier.html(properties.value[0].value);
        inputValue.value = replaceSegmentsWithPlaceholders(beautifiedHtml);
        restoreScroll();
      }
    }, { immediate: true });

    // Detect if cursor is on an HTML element (excluding internal tags like spmtranslation)
    const isOnHtmlElement = computed(() => {
      if (!selectedElementInfo.value) return false;
      const tagName = selectedElementInfo.value.tagName.toLowerCase();
      // Exclude internal system tags
      const excludedTags = ['spmtranslation'];
      return !excludedTags.includes(tagName);
    });

    // Check if current element has existing segments
    const hasExistingSegments = computed(() => currentElementSegments.value !== null
      && Array.isArray(currentElementSegments.value)
      && currentElementSegments.value.length > 0);

    // Check if current element has existing loop
    const hasExistingLoop = computed(() => currentLoopConfig.value !== null);

    // Check if current element is a products collection (cannot be modified)
    const isProductsCollection = computed(() => currentLoopConfig.value?.collection === 'products');

    // Open loop modal
    const openLoopModal = () => {
      if (!selectedElementInfo.value) return;

      // Check if element already has a loop configuration
      const { editor } = codeMirrorEditorRef.value;
      const line = selectedElementInfo.value.startLine;
      const lineContent = editor.getLine(line);

      // Check for existing s-collection and s-item attributes
      const collectionMatch = lineContent.match(/s-collection="([^"]+)"/);
      const itemMatch = lineContent.match(/s-item="([^"]+)"/);

      const orderMatch = lineContent.match(/s-order="([^"]+)"/);
      const nbMatch = lineContent.match(/s-nb="([^"]+)"/);

      if (collectionMatch && itemMatch) {
        const config: LoopConfiguration = {
          enabled: true,
          collection: collectionMatch[1],
          iterator: itemMatch[1],
        };
        if (orderMatch) {
          try {
            config.order = JSON.parse(orderMatch[1].replace(/&quot;/g, '"'));
          } catch (e) {
            // Ignore invalid JSON
          }
        }
        if (nbMatch) {
          config.limit = parseInt(nbMatch[1], 10);
        }
        currentLoopConfig.value = config;
      } else {
        currentLoopConfig.value = null;
      }

      isLoopModalVisible.value = true;
    };

    // Handle loop save
    const handleLoopSave = (config: LoopConfiguration | null) => {
      if (!selectedElementInfo.value || !codeMirrorEditorRef.value) return;

      const { editor } = codeMirrorEditorRef.value;
      const { startLine } = selectedElementInfo.value;
      const { endLine } = selectedElementInfo.value;
      const lineContent = editor.getLine(startLine);

      // Get old iterator if exists (from s-item or from ITERATION placeholder)
      let oldIterator: string | null = null;
      const oldItemMatch = lineContent.match(/s-item="([^"]+)"/);
      if (oldItemMatch) {
        [, oldIterator] = oldItemMatch;
      } else {
        const iterationMatch = lineContent.match(/ITERATION ([a-zA-Z_][a-zA-Z0-9_]*)/);
        if (iterationMatch) {
          const placeholder = `ITERATION ${iterationMatch[1]}`;
          const data = collectionsMap.value.get(placeholder);
          if (data) {
            oldIterator = data.item;
          }
        }
      }

      // Find the opening tag
      const tagMatch = lineContent.match(/(<[a-zA-Z][a-zA-Z0-9]*[^>]*)(>)/);

      if (tagMatch) {
        const tagPart = tagMatch[1];
        const tagEndPos = (tagMatch.index || 0) + tagMatch[0].length;

        // Remove existing s-collection, s-item, s-order, s-nb attributes and ITERATION placeholder
        let cleanedTag = tagPart.replace(/\s*s-collection="[^"]*"/, '');
        cleanedTag = cleanedTag.replace(/\s*s-item="[^"]*"/, '');
        cleanedTag = cleanedTag.replace(/\s*s-order="[^"]*"/, '');
        cleanedTag = cleanedTag.replace(/\s*s-nb="[^"]*"/, '');
        cleanedTag = cleanedTag.replace(/\s*ITERATION [a-zA-Z_][a-zA-Z0-9_]*\s*/, '');

        let newLine;
        if (config) {
          // Determine placeholder using iterator name
          const placeholder = `ITERATION ${config.iterator}`;

          // Parse collection data
          let collectionData;
          try {
            collectionData = JSON.parse(config.collection.replace(/&quot;/g, '"'));
          } catch {
            collectionData = config.collection;
          }

          // Store in map with order and limit
          const mapData: any = { collection: collectionData, item: config.iterator };
          if (config.order) {
            mapData.order = config.order;
          }
          if (config.limit) {
            mapData.limit = config.limit;
          }
          collectionsMap.value.set(placeholder, mapData);

          // Reconstruct line with placeholder
          const beforeTag = lineContent.substring(0, tagMatch.index || 0);
          const afterTag = lineContent.substring(tagEndPos);
          newLine = `${beforeTag}${cleanedTag} ${placeholder}>${afterTag}`;
        } else {
          // Remove loop (no placeholder)
          const beforeTag = lineContent.substring(0, tagMatch.index || 0);
          const afterTag = lineContent.substring(tagEndPos);
          newLine = `${beforeTag}${cleanedTag}>${afterTag}`;
        }

        // Replace the line
        editor.replaceRange(newLine, { line: startLine, ch: 0 }, { line: startLine, ch: lineContent.length });

        // If iterator changed, replace all variables in the element content
        if (config && oldIterator && oldIterator !== config.iterator) {
          // Also replace variables on the first line after the tag
          const firstLineAfterTag = editor.getLine(startLine);
          const regex = new RegExp(`\\{var=${oldIterator}\\.([^}]+)\\}`, 'g');
          const updatedFirstLine = firstLineAfterTag.replace(regex, `{var=${config.iterator}.$1}`);
          if (firstLineAfterTag !== updatedFirstLine) {
            editor.replaceRange(updatedFirstLine, { line: startLine, ch: 0 }, { line: startLine, ch: firstLineAfterTag.length });
          }

          // Track nested collections to avoid replacing their variables
          let nestedCollectionDepth = 0;
          let inNestedCollection = false;

          // Get all content between start and end lines
          for (let i = startLine + 1; i <= endLine; i++) {
            const currentLine = editor.getLine(i);

            // Check if we're entering a nested collection
            if (currentLine.includes('s-collection=')) {
              nestedCollectionDepth += 1;
              inNestedCollection = true;
            }

            // Check if we're exiting a nested collection (closing tag)
            // Simple heuristic: count opening and closing tags
            const openingTags = (currentLine.match(/<[a-zA-Z][a-zA-Z0-9]*[^>]*>/g) || []).length;
            const closingTags = (currentLine.match(/<\/[a-zA-Z][a-zA-Z0-9]*>/g) || []).length;

            if (inNestedCollection && closingTags > openingTags) {
              nestedCollectionDepth -= (closingTags - openingTags);
              if (nestedCollectionDepth <= 0) {
                nestedCollectionDepth = 0;
                inNestedCollection = false;
              }
            }

            // Only replace variables if we're not inside a nested collection
            if (!inNestedCollection) {
              // Replace all occurrences of {var=oldIterator.xxx} with {var=newIterator.xxx}
              const updatedLine = currentLine.replace(regex, `{var=${config.iterator}.$1}`);

              if (currentLine !== updatedLine) {
                editor.replaceRange(updatedLine, { line: i, ch: 0 }, { line: i, ch: currentLine.length });
              }
            }
          }
        }

        // Re-add widgets after modification
        setTimeout(() => {
          addPlaceholderWidgets(editor);
        }, 100);

        // Trigger value change
        handleValueChange();
      }

      isLoopModalVisible.value = false;
      currentLoopConfig.value = config;
    };

    // Handle loop delete
    const handleLoopDelete = () => {
      handleLoopSave(null);
    };

    // Open conditional modal
    const openConditionalModal = () => {
      if (!selectedElementInfo.value) return;

      // If no segments exist, start with empty structure
      if (!currentElementSegments.value) {
        currentElementSegments.value = [[{}]];
      }

      // Detect collection context and create virtual element for the modal
      const collection = detectCollectionContext(selectedElementInfo.value.startLine);
      if (collection) {
        // Extract the last segment of the collection path
        // order.products -> products, contact.custom.customers_veterinaires -> customers_Lres
        const collectionSegments = collection.name.split('.');
        const lastSegment = collectionSegments[collectionSegments.length - 1];

        // Theme is always the last segment (used to load properties)
        // Iterator is used as prefix for variables (e.g., "vet.first_name")
        const iterator = collection.iterator || lastSegment;

        // Create virtual element with s-collection and s-item attributes
        const virtualElement = document.createElement('div');
        virtualElement.setAttribute('s-collection', JSON.stringify({
          name: collection.name,
          iterator,
        }));
        virtualElement.setAttribute('s-item', iterator);
        selectedHtmlElement.value = virtualElement;
      } else {
        selectedHtmlElement.value = null;
      }

      isConditionalModalVisible.value = true;
    };

    // Handle conditional save
    const handleConditionalSave = (segments: any) => {
      if (!selectedElementInfo.value || !codeMirrorEditorRef.value) return;

      const { editor } = codeMirrorEditorRef.value;
      const line = selectedElementInfo.value.startLine;
      const lineContent = editor.getLine(line);

      // Find the opening tag
      const tagMatch = lineContent.match(/(<[a-zA-Z][a-zA-Z0-9]*[^>]*)(>)/);

      if (tagMatch) {
        const tagPart = tagMatch[1];
        const tagEndPos = (tagMatch.index || 0) + tagMatch[0].length;

        // Check if there's an existing placeholder
        const existingConditionMatch = lineContent.match(/CONDITION (\d+)/);
        const existingPlaceholderMatch = tagPart.match(/s-segments="\[([^\]]+)\]"/);

        let placeholder: string;
        if (existingConditionMatch) {
          placeholder = `CONDITION ${existingConditionMatch[1]}`;
        } else if (existingPlaceholderMatch) {
          [, placeholder] = existingPlaceholderMatch;
        } else {
          conditionCounter += 1;
          placeholder = `CONDITION ${conditionCounter}`;
        }

        // Store or update the condition in the map
        conditionsMap.value.set(placeholder, segments);

        // Remove existing s-segments attribute from tag
        let cleanedTag = tagPart.replace(/\s*s-segments="[^"]*"/, '');

        // Remove existing CONDITION placeholder from tag
        cleanedTag = cleanedTag.replace(/\s*CONDITION \d+\s*/, '');

        // Reconstruct the line with placeholder as attribute (before >)
        const beforeTag = lineContent.substring(0, tagMatch.index || 0);
        const afterTag = lineContent.substring(tagEndPos);

        const newLine = `${beforeTag}${cleanedTag} ${placeholder}>${afterTag}`;

        // Replace the line
        editor.replaceRange(newLine, { line, ch: 0 }, { line, ch: lineContent.length });

        // Re-add widgets after modification
        setTimeout(() => {
          addPlaceholderWidgets(editor);
        }, 100);

        // Trigger value change
        handleValueChange();
      }

      isConditionalModalVisible.value = false;
    };

    // Handle conditional delete
    const handleConditionalDelete = () => {
      if (!selectedElementInfo.value || !codeMirrorEditorRef.value) return;

      const { editor } = codeMirrorEditorRef.value;
      const line = selectedElementInfo.value.startLine;
      const lineContent = editor.getLine(line);

      // Find the opening tag
      const tagMatch = lineContent.match(/(<[a-zA-Z][a-zA-Z0-9]*[^>]*)(>)/);

      if (tagMatch) {
        const beforeTag = lineContent.substring(0, tagMatch.index);
        const tagPart = tagMatch[1];
        const afterTag = lineContent.substring(tagMatch.index! + tagMatch[0].length);

        // Check if there's a CONDITION placeholder to remove from the map
        const existingConditionMatch = tagPart.match(/CONDITION (\d+)/);
        if (existingConditionMatch) {
          const placeholder = `CONDITION ${existingConditionMatch[1]}`;
          conditionsMap.value.delete(placeholder);
        }

        // Remove s-segments attribute if present
        let cleanedTag = tagPart.replace(/\s*s-segments="[^"]*"/, '');

        // Remove CONDITION placeholder
        cleanedTag = cleanedTag.replace(/\s*CONDITION \d+\s*/, '');

        // Build new line without condition
        const newLine = `${beforeTag}${cleanedTag}>${afterTag}`;

        // Replace the line
        editor.replaceRange(newLine, { line, ch: 0 }, { line, ch: lineContent.length });

        // Re-add widgets after modification
        setTimeout(() => {
          addPlaceholderWidgets(editor);
        }, 100);

        // Trigger value change
        handleValueChange();
      }

      isConditionalModalVisible.value = false;
    };

    const openCodeModal = () => {
      showCodeModal.value = true;
    };

    const handleModalApply = (code: string) => {
      inputValue.value = code;
      handleValueChange();
    };

    return {
      t,
      inputValue,
      enrichedVariables,
      codeMirrorEditorRef,
      insertContent,
      onBlur,
      isOnHtmlElement,
      hasExistingSegments,
      hasExistingLoop,
      isProductsCollection,
      onCursorActivity,
      openConditionalModal,
      isConditionalModalVisible,
      selectedHtmlElement,
      currentElementSegments,
      handleConditionalSave,
      handleConditionalDelete,
      openLoopModal,
      isLoopModalVisible,
      currentLoopConfig,
      handleLoopSave,
      handleLoopDelete,
      showCodeModal,
      openCodeModal,
      handleModalApply,
      // Redirect picker
      isCursorInHref,
      isRedirectPickerVisible,
      currentHrefValue,
      openRedirectPicker,
      handleRedirectConfirm,
      handleRedirectRemove,
    };
  },
});
</script>

<style scoped lang="scss">
:deep() .CodeMirror {
  height: 500px !important;
}

.conditional-button {
  .conditional-icon {
    font-size: 0.9rem;
    color: #8b5cf6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.loop-button-wrapper {
  display: inline-block;
  cursor: not-allowed;
}

.loop-button {
  .loop-icon {
    font-size: 0.9rem;
    color: #06b6d4;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

/* Styles pour le wrapper CodeMirror avec bouton d'élargissement */
.codemirror-wrapper {
  position: relative;
  width: 100%;
}

.codemirror-header {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;

  .expand-code-btn {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 6px 8px;
    cursor: pointer;
    color: #607D8B;
    font-size: 12px;
    transition: all 0.2s ease;

    &:hover {
      background: #f8f9fa;
      color: #495057;
      transform: scale(1.05);
    }
  }
}
</style>
